import { makeObservable, observable, action } from 'mobx';
import uniq from 'lodash/uniq';
import flatten from 'lodash/flatten';

import { ContractViewModelData } from 'chaika-frontend-types/frontend-cms';

import { SimpleList } from '@store/models/SimpleList';
import { Optional } from 'chaika-common-types';
import { ContractStatus } from 'chaika-enums';

export interface FilterParams {
    query?: string;
    payerId?: string;
    legalEntityId?: string | string[];
    legalEntityIds?: string[];
    status?: ContractStatus;
    statuses?: ContractStatus[];
    payerIds?: string[];
    isMain?: boolean[];
}

export class ContractList extends SimpleList<ContractViewModelData, FilterParams> {
    public static Name = 'contractList' as const;

    @observable private loadedLegalEntitiesIdsContracts = new Set<string>();

    constructor(params?: ContractViewModelData[]) {
        super(params);
        makeObservable(this);
    }

    public getContractsLegalEntityIds(contractIds: string[]): string[] {
        const contracts = this.getValuesByIds(contractIds);
        return uniq(flatten(contracts.map(({ legalEntityIds }) => legalEntityIds)));
    }

    public getNotLoadedLegalEntitiesIds(legalEntityIds: string[]): string[] {
        return legalEntityIds.filter(id => !this.loadedLegalEntitiesIdsContracts.has(id));
    }

    @action
    public addLoadedLegalEntitiesIds(legalEntityIds: string[]): void {
        legalEntityIds.forEach(id => {
            this.loadedLegalEntitiesIdsContracts.add(id);
        });
    }

    public getLegalEntityContracts(legalEntityId: string): ContractViewModelData[] {
        return this.getFilteredValues({ legalEntityId, status: ContractStatus.Active });
    }

    public getLegalEntitiesContracts(legalEntityId: string[]): ContractViewModelData[] {
        return this.getFilteredValues({ legalEntityId, status: ContractStatus.Active });
    }

    public getAllLegalEntityContracts(legalEntityId: string): ContractViewModelData[] {
        return this.getFilteredValues({ legalEntityId });
    }

    public getLegalEntityMainContractId(legalEntityId: string): Optional<string> {
        return this.getLegalEntityContracts(legalEntityId).find(({ isMain }) => isMain)?.id;
    }

    protected filterValue(
        value: ContractViewModelData,
        { legalEntityId, payerId, status, statuses, legalEntityIds, payerIds, isMain, query }: FilterParams,
    ): boolean {
        return (
            this.filterFieldValueIncludesString(value, query, 'num') &&
            this.filterFieldValue(value, payerId, 'payerId') &&
            this.filterFieldValue(value, status, 'status') &&
            this.filterFieldValueByArray(value, statuses, 'status') &&
            this.filterFieldValueByArray(value, isMain, 'isMain') &&
            this.filterArrayFieldValueContainsAll(value, legalEntityIds, 'legalEntityIds') &&
            this.filterFieldValueByArray(value, payerIds, 'payerId') &&
            (Array.isArray(legalEntityId)
                ? this.filterArrayFieldValueByArray(value, legalEntityId, 'legalEntityIds') ||
                  this.filterFieldValueByArray(value, legalEntityId, 'payerId')
                : this.filterArrayFieldValue(value, legalEntityId, 'legalEntityIds') ||
                  this.filterFieldValue(value, legalEntityId, 'payerId'))
        );
    }

    private filterArrayFieldValueContainsAll(
        value: ContractViewModelData,
        filterValue: string[] | undefined,
        fieldName: keyof ContractViewModelData,
    ): boolean {
        if (!filterValue || filterValue.length === 0) {
            return true;
        }

        const fieldValuesSet = new Set((value[fieldName] as string[]) ?? []);
        return filterValue.every(filterItem => fieldValuesSet.has(filterItem));
    }
}

export const contractList = new ContractList();
